/**
 * Hydration tracker — pure helpers.
 *
 * Water is a DAILY-TARGET discipline (it resets each calendar day), unlike the
 * cumulative Fitness parameters. So its economy XP is defined per-day and
 * CAPPED at the day's target: a perfect day earns `DAILY_HYDRATION_XP`, and
 * over-drinking earns nothing extra. That cap is what makes the XP farm-proof —
 * you can't mint points by logging 50 fake drinks.
 *
 * `sumHydrationXP` is the SINGLE definition of "Hydration's economy XP" so the
 * user total, the Personal domain credit (`useUserStats`) and the Nutrition area
 * rank (`useDisciplines`) all agree on one number — the same "one action, every
 * ledger" contract Breathing uses.
 */

import { POINTS } from './levelCalculation';

export type HydrationUnit = 'ml' | 'oz';

/** A single day's water row (the shape stored per row in `hydration_logs`). */
export interface HydrationDay {
  date: string; // local YYYY-MM-DD
  intakeMl: number;
  targetMl: number;
}

/** One logged drink within a day. */
export interface HydrationEntry {
  ml: number;
  at: string; // ISO timestamp
}

export const DEFAULT_TARGET_ML = 2500;
export const MIN_TARGET_ML = 500;
export const MAX_TARGET_ML = 10000;

/** Economy XP awarded for a fully-met hydration day (tunable). */
export const DAILY_HYDRATION_XP = 50;

export const ML_PER_OZ = 29.5735;

/** Quick-add drink presets, in millilitres. */
export const DRINK_PRESETS_ML = [250, 500, 750] as const;

/** Clamp a raw target into the supported range. */
/**
 * A typed drink amount, in the unit the box shows, as millilitres.
 * ⚠️ The custom box reads "oz" in ounces mode; until 2026-10-05 its number was
 * logged as ml (16 oz → 16 ml). `null` for anything that is not a positive amount
 * — including one that ROUNDS to 0 ml (0.4 ml, 0.01 oz): the store refuses a 0 ml
 * drink, and the panel used to report that refusal as a connection error.
 */
export function amountToMl(value: number, unit: HydrationUnit): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  const ml = Math.round(unit === 'oz' ? value * ML_PER_OZ : value);
  return ml > 0 ? ml : null;
}

export function clampTarget(ml: number): number {
  if (isNaN(ml)) return DEFAULT_TARGET_ML;
  return Math.min(MAX_TARGET_ML, Math.max(MIN_TARGET_ML, Math.round(ml)));
}

/**
 * Economy XP for ONE day — proportional to goal completion, capped at 100%.
 * `dayHydrationXP(2500, 2500) === DAILY_HYDRATION_XP`; over-target stays capped.
 */
export function dayHydrationXP(intakeMl: number, targetMl: number): number {
  const intake = Math.max(0, intakeMl || 0);
  const target = Math.max(1, targetMl || DEFAULT_TARGET_ML);
  const ratio = Math.min(1, intake / target);
  // Config-backed cap (console → XP economy); the const above is the default.
  return Math.round(ratio * (POINTS.TRACKER_HYDRATION_DAILY ?? DAILY_HYDRATION_XP));
}

/** Total Hydration economy XP across a set of days (the single source of truth). */
export function sumHydrationXP(days: ReadonlyArray<HydrationDay>): number {
  return days.reduce((sum, d) => sum + dayHydrationXP(d.intakeMl, d.targetMl), 0);
}

/** Did this day meet its goal? */
export function isDayMet(day: HydrationDay): boolean {
  return (day.intakeMl || 0) >= (day.targetMl || DEFAULT_TARGET_ML) && (day.targetMl || 0) > 0;
}

/** Millilitres still to drink today (never negative). */
export function remainingMl(intakeMl: number, targetMl: number): number {
  return Math.max(0, (targetMl || 0) - (intakeMl || 0));
}

/** 0..100 progress toward today's goal. */
export function hydrationProgress(intakeMl: number, targetMl: number): number {
  const target = Math.max(1, targetMl || DEFAULT_TARGET_ML);
  return Math.min(100, Math.max(0, ((intakeMl || 0) / target) * 100));
}

// ── date helpers (pure; "today" is always passed in so frozen clocks/TZ don't bite) ──

/** Local YYYY-MM-DD for a Date (defaults to now — callers in UI pass their own). */
export function toLocalYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The calendar day `n` days before `ymd` (n may be negative). Pure string math. */
export function shiftYMD(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Consecutive days meeting goal, ending at `todayStr`. An in-progress today that
 * hasn't hit goal yet does NOT break the streak — it just isn't counted, so the
 * streak reflects yesterday's standing until today is met.
 */
export function hydrationStreak(days: ReadonlyArray<HydrationDay>, todayStr: string): number {
  const met = new Map<string, boolean>();
  for (const d of days) met.set(d.date, isDayMet(d));

  let streak = 0;
  let cursor = met.get(todayStr) ? todayStr : shiftYMD(todayStr, -1);
  while (met.get(cursor)) {
    streak++;
    cursor = shiftYMD(cursor, -1);
  }
  return streak;
}

/** Format a millilitre amount in the chosen unit (e.g. "2.5 L", "84 oz"). */
export function formatVolume(ml: number, unit: HydrationUnit): string {
  const v = Math.max(0, ml || 0);
  if (unit === 'oz') return `${Math.round(v / ML_PER_OZ)} oz`;
  /* ⚠️ Two decimals, trailing zeros dropped. One decimal showed a 2,950 ml target
     (the calculator rounds to 50 ml) as "3.0 L" — a target the user never set. */
  if (v >= 1000) return `${Number((v / 1000).toFixed(2))} L`;
  return `${Math.round(v)} ml`;
}
